import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ArrowLeft, Check, Sparkles, Crown, Loader2, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const levelInfo = {
  P4: { label: 'Primary 4', color: 'emerald', description: 'Foundation grammar & vocabulary' },
  P5: { label: 'Primary 5', color: 'amber', description: 'Intermediate PSLE preparation' },
  P6: { label: 'Primary 6', color: 'blue', description: 'Full PSLE exam readiness' }
};

const SubscriptionPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const data = await api.getSubscriptionStatus();
      setStatus(data);
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      toast.error('Failed to load subscription info');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (level) => {
    setCheckoutLoading(level);
    try {
      const originUrl = window.location.origin;
      const data = await api.createCheckout(level, originUrl);
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout. Please try again.');
      setCheckoutLoading(null);
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return '';
    return new Date(isoString).toLocaleDateString('en-SG', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="bg-white border-b-2 border-slate-200 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/dashboard')}
                variant="outline"
                size="sm"
                className="rounded-full"
                data-testid="subscription-back-button"
              >
                <ArrowLeft className="w-4 h-4" />
              </Button>
              <div>
                <h1 className="text-xl font-heading font-bold text-emerald-600" data-testid="subscription-title">
                  Subscriptions
                </h1>
                <p className="text-sm text-slate-500">Unlock unlimited practice</p>
              </div>
            </div>
            <img
              src="https://customer-assets.emergentagent.com/job_e77e6e6f-0eb9-4cf5-990d-5085ee85d2a5/artifacts/o9we2o4z_file_00000000b8947207b6be688fc35a0ba2.png"
              alt="Prof. Scaly"
              className="w-12 h-12 object-contain"
            />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {loading ? (
          <div className="text-center py-16" data-testid="subscription-loading">
            <Loader2 className="w-16 h-16 animate-spin text-emerald-500 mx-auto mb-4" />
            <p className="text-lg text-slate-600">Loading subscription info...</p>
          </div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="text-center mb-10">
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-bold mb-4">
                <Crown className="w-4 h-4" strokeWidth={2.5} />
                Prof.Scaly Premium
              </div>
              <h2 className="text-4xl sm:text-5xl font-heading font-bold text-slate-800 mb-3" data-testid="subscription-hero-title">
                Unlock Unlimited Practice
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                Every student gets <span className="font-bold text-emerald-600">50 free practices per topic</span>. Subscribe to your level for <span className="font-bold">unlimited AI-generated questions</span> for a full year.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {['P4', 'P5', 'P6'].map((level, idx) => {
                const info = levelInfo[level];
                const sub = status?.subscriptions?.find(s => s.level === level);
                const isActive = sub?.is_active;
                const isCurrentLevel = user?.level === level;
                const pricing = status?.pricing?.[level];

                const colorClasses = {
                  emerald: {
                    border: 'border-emerald-300', bg: 'bg-emerald-50', text: 'text-emerald-700',
                    badge: 'bg-emerald-100 text-emerald-700',
                    button: 'bg-emerald-500 hover:bg-emerald-600 border-emerald-700'
                  },
                  amber: {
                    border: 'border-amber-300', bg: 'bg-amber-50', text: 'text-amber-700',
                    badge: 'bg-amber-100 text-amber-700',
                    button: 'bg-amber-500 hover:bg-amber-600 border-amber-700'
                  },
                  blue: {
                    border: 'border-blue-300', bg: 'bg-blue-50', text: 'text-blue-700',
                    badge: 'bg-blue-100 text-blue-700',
                    button: 'bg-blue-500 hover:bg-blue-600 border-blue-700'
                  }
                }[info.color];

                return (
                  <motion.div
                    key={level}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 * idx }}
                    whileHover={{ scale: 1.02, y: -4 }}
                  >
                    <Card
                      className={`p-8 rounded-3xl border-2 ${isCurrentLevel ? colorClasses.border : 'border-slate-200'} ${isCurrentLevel ? colorClasses.bg : 'bg-white'} h-full flex flex-col relative`}
                      data-testid={`plan-card-${level}`}
                    >
                      {isCurrentLevel && (
                        <div className={`absolute -top-3 left-1/2 -translate-x-1/2 ${colorClasses.badge} px-3 py-1 rounded-full text-xs font-bold`}>
                          Your Level
                        </div>
                      )}

                      <div className="text-center mb-6">
                        <div className={`inline-flex items-center gap-2 ${colorClasses.badge} px-3 py-1 rounded-full text-xs font-bold mb-3`}>
                          {info.label}
                        </div>
                        <h3 className={`text-2xl font-heading font-bold ${colorClasses.text} mb-1`}>
                          {info.label}
                        </h3>
                        <p className="text-sm text-slate-600 mb-4">{info.description}</p>

                        <div className="mb-2">
                          <span className="text-5xl font-heading font-bold text-slate-800" data-testid={`plan-price-${level}`}>
                            SGD ${pricing?.amount?.toFixed(2) || '2.00'}
                          </span>
                          <span className="text-slate-500 text-sm ml-1">/ year</span>
                        </div>
                      </div>

                      <ul className="space-y-3 mb-6 flex-1">
                        {['Unlimited AI-generated questions', 'All 7 practice modules unlocked', 'Prof.Scaly AI tutor chat', 'Save unlimited favorites', 'Progress tracking'].map((feature, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <div className={`${colorClasses.badge} rounded-full p-1 mt-0.5`}>
                              <Check className="w-3 h-3" strokeWidth={3} />
                            </div>
                            <span className="text-sm text-slate-700">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      {isActive ? (
                        <div className="space-y-2" data-testid={`active-subscription-${level}`}>
                          <div className={`${colorClasses.badge} rounded-2xl p-3 text-center`}>
                            <div className="flex items-center justify-center gap-2 mb-1">
                              <Crown className="w-4 h-4" strokeWidth={2.5} />
                              <span className="text-sm font-bold">Active</span>
                            </div>
                            <div className="flex items-center justify-center gap-1 text-xs">
                              <Calendar className="w-3 h-3" />
                              <span>Expires {formatDate(sub.expires_at)}</span>
                            </div>
                          </div>
                          <Button
                            onClick={() => handleSubscribe(level)}
                            disabled={checkoutLoading === level}
                            variant="outline"
                            className="w-full rounded-full py-5 text-sm font-bold border-2"
                            data-testid={`extend-button-${level}`}
                          >
                            {checkoutLoading === level ? (
                              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
                            ) : 'Extend Subscription'}
                          </Button>
                        </div>
                      ) : (
                        <Button
                          onClick={() => handleSubscribe(level)}
                          disabled={checkoutLoading === level}
                          className={`w-full ${colorClasses.button} text-white rounded-full py-6 text-base font-bold tactile-button border-b-4`}
                          data-testid={`subscribe-button-${level}`}
                        >
                          {checkoutLoading === level ? (
                            <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Redirecting...</>
                          ) : (
                            <><Sparkles className="w-5 h-5 mr-2" strokeWidth={2.5} />Subscribe</>
                          )}
                        </Button>
                      )}
                    </Card>
                  </motion.div>
                );
              })}
            </div>

            <div className="mt-10 bg-white rounded-3xl border-2 border-slate-200 p-6">
              <h3 className="text-lg font-heading font-bold text-slate-800 mb-3">About Prof.Scaly Premium</h3>
              <ul className="space-y-2 text-sm text-slate-600">
                <li>• Every student gets <span className="font-bold text-emerald-600">50 free practices per topic</span> (7 topics × 3 levels).</li>
                <li>• A subscription unlocks unlimited AI-generated questions for <span className="font-bold">all 7 topics at the subscribed level</span>.</li>
                <li>• Subscription is valid for <span className="font-bold">365 days from the day of payment</span>.</li>
                <li>• Secure payments processed by Stripe. Test card: <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">4242 4242 4242 4242</code>, any future date, any CVC.</li>
              </ul>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default SubscriptionPage;
